/**
 * `SectionCard`'s header-state rule: a collapsible card carries exactly one
 * of `count` and `summary`.
 *
 * WHY IT LIVES HERE rather than inside the component it guards. This repo
 * mounts no components in unit tests — `components/ui/*` imports through the
 * `@/...` alias, which is declared to webpack and jsconfig but not to jest
 * (see `ConditionUpdate.test.js`). A rule buried in a render body is a rule
 * with no behavioural test at all, only string matches. Out here it is a
 * plain function with no imports, and the invariant gets tested for real.
 *
 * The rule itself, the decision behind it, and the reason the two violations
 * are punished differently are documented on `SectionCard` in
 * `components/identityForm.jsx`. Read that before changing this.
 */

const has = (v) => v !== null && v !== undefined;

/**
 * Throws if a collapsible `SectionCard` has two header states, or — outside
 * production — if it has none.
 *
 * The asymmetry is deliberate; see `SectionCard`'s comment. Briefly: two
 * states is a state the component cannot render at all, no header state is a
 * card it renders perfectly well and a guideline it breaks. There is no
 * ErrorBoundary in this app, so a production throw takes down a whole page,
 * and it takes it down in front of a player rather than the developer who
 * left the prop off.
 *
 * `title` is in the message on purpose: a page holds several of these cards,
 * and a stack trace through a shared primitive names the primitive, never the
 * caller that got it wrong.
 */
export function assertSectionHeaderState({ title, count, summary }) {
  // Both — incoherent, and fatal in every environment.
  if (has(count) && has(summary)) {
    throw new Error(
      `SectionCard "${title}": pass \`count\` OR \`summary\`, never both — ` +
        "`count` is for log sections with countable entries, `summary` for " +
        "settings sections whose state is not a number.",
    );
  }

  // Neither — a real violation, but a survivable one. CRA fixes
  // `process.env.NODE_ENV` at build time and jest sets it to "test", so this
  // fires in every unit test and every dev server, which is where a developer
  // meets it. In a production bundle the comparison is constant-folded to
  // false and the branch is dropped entirely: the card renders a bare header
  // rather than nothing at all.
  if (!has(count) && !has(summary) && process.env.NODE_ENV !== "production") {
    throw new Error(
      `SectionCard "${title}": a collapsible card must carry \`count\` ` +
        "(log sections) or `summary` (settings sections). A fold that does " +
        "not say what is inside it is worse than no fold at all (#378); a " +
        'settings section with nothing to report passes summary="—".',
    );
  }
}

/**
 * The `summary` a FORM-type section shows on its folded header (#457/#458).
 *
 * Identity & Vision's three sections and Reflections' Mental Performance are
 * neither logs nor settings: they are a fixed set of questions the player
 * answers over time. Their state is not a number of entries — `count` would
 * claim they accumulate, which they do not — so they report progress instead:
 * `Not started`, `4 of 8`, `Complete`. All three are derived, and all three
 * change as the player types, which is the rule the header-state obligation
 * actually cares about: a hardcoded badge is a fold that says nothing wearing
 * a badge that says something.
 *
 * `answers` is ONE VALUE PER QUESTION, in whatever shape the page holds it.
 * An array-valued question (Player Vision's three ambitions, its preferred
 * formats) is one question and counts as answered when any element is, which
 * matches `identityDraft.js`'s "array fields are dirty as a unit".
 *
 * @param {Array<unknown>} answers
 * @returns {string} a badge string, never empty
 */
export function sectionFillSummary(answers) {
  const filled = answers.filter(isAnswered).length;
  if (filled === 0) return "Not started";
  if (filled === answers.length) return "Complete";
  return `${filled} of ${answers.length}`;
}

/** Has this question been answered? Blank and whitespace-only are not. */
function isAnswered(value) {
  if (Array.isArray(value)) return value.some(isAnswered);
  if (typeof value === "string") return value.trim() !== "";
  return value !== null && value !== undefined;
}
